/**
 * BiometricBadges - Inline wearable device status for roster cards
 *
 * Displays connection status and key metrics from:
 * - Whoop (strain, recovery)
 * - Oura (readiness, sleep)
 * - Neuphoria (mental state, focus)
 * - Garmin (VO2 max, body battery)
 * - Apple Watch (activity rings)
 *
 * OLED dark design matching roster card aesthetics.
 */

import React, { useMemo } from 'react';
import {
  Activity,
  Brain,
  Moon,
  Watch,
  Zap,
  Heart,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { generateBiometricProfile, getStatusColor } from '../utils/biometricDataGenerator';

/**
 * Device icon mapping
 */
const DeviceIcon = ({ device, size = 14, connected }) => {
  const iconProps = { size, className: connected ? '' : 'opacity-30' };

  switch (device) {
    case 'whoop':
      return <Zap {...iconProps} />;
    case 'oura':
      return <Moon {...iconProps} />;
    case 'neuphoria':
      return <Brain {...iconProps} />;
    case 'garmin':
      return <Activity {...iconProps} />;
    case 'appleWatch':
      return <Watch {...iconProps} />;
    default:
      return <Heart {...iconProps} />;
  }
};

/**
 * Compact device badge for list view
 */
const DeviceBadge = ({ device, data, showLabel = false }) => {
  const isConnected = data?.connected;

  return (
    <div
      className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md transition-all ${
        isConnected
          ? 'bg-white/[0.08] hover:bg-white/[0.12]'
          : 'bg-white/[0.02]'
      }`}
      title={`${data?.deviceName || device}: ${isConnected ? 'Connected' : 'Not connected'}`}
      style={{ borderLeft: isConnected ? `2px solid ${data?.color}` : '2px solid transparent' }}
    >
      <DeviceIcon device={device} size={12} connected={isConnected} />
      {showLabel && (
        <span className={`text-[10px] ${isConnected ? 'text-white/70' : 'text-white/30'}`}>
          {data?.deviceName?.charAt(0) || device.charAt(0).toUpperCase()}
        </span>
      )}
    </div>
  );
};

/**
 * Readiness score with glow effect
 */
const ReadinessScore = ({ score, size = 'md' }) => {
  if (score === null || score === undefined) return null;

  const getColor = () => {
    if (score >= 85) return { text: 'text-emerald-400', glow: 'rgba(16, 185, 129, 0.5)' };
    if (score >= 70) return { text: 'text-green-400', glow: 'rgba(34, 197, 94, 0.4)' };
    if (score >= 55) return { text: 'text-amber-400', glow: 'rgba(251, 191, 36, 0.4)' };
    return { text: 'text-red-400', glow: 'rgba(239, 68, 68, 0.4)' };
  };

  const { text, glow } = getColor();
  const textSize = size === 'lg' ? 'text-xl' : size === 'sm' ? 'text-sm' : 'text-lg';

  return (
    <span
      className={`font-bold ${text} ${textSize}`}
      style={{ textShadow: `0 0 12px ${glow}` }}
    >
      {score}%
    </span>
  );
};

/**
 * BiometricBadgesCompact - Minimal inline display for roster cards
 * Shows device connection dots and primary readiness score
 */
export const BiometricBadgesCompact = ({ playerId, playerName = '', className = '' }) => {
  const biometrics = useMemo(
    () => generateBiometricProfile(playerId, playerName),
    [playerId, playerName]
  );

  if (!biometrics.hasAnyDevice) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <WifiOff size={12} className="text-white/20" />
        <span className="text-[10px] text-white/30">No devices</span>
      </div>
    );
  }

  const connectedDevices = Object.entries(biometrics.devices)
    .filter(([_, data]) => data.connected)
    .slice(0, 3); // Show max 3 devices

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Device connection indicators */}
      <div className="flex items-center gap-0.5">
        {connectedDevices.map(([device, data]) => (
          <div
            key={device}
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: data.color }}
            title={data.deviceName}
          />
        ))}
        {biometrics.activeDeviceCount > 3 && (
          <span className="text-[9px] text-white/40 ml-0.5">
            +{biometrics.activeDeviceCount - 3}
          </span>
        )}
      </div>

      {/* Composite readiness */}
      {biometrics.compositeReadiness && (
        <ReadinessScore score={biometrics.compositeReadiness} size="sm" />
      )}
    </div>
  );
};

/**
 * BiometricBadgesRow - Horizontal row of device badges with metrics
 * For use in grid card views
 */
export const BiometricBadgesRow = ({ playerId, playerName = '', className = '' }) => {
  const biometrics = useMemo(
    () => generateBiometricProfile(playerId, playerName),
    [playerId, playerName]
  );

  if (!biometrics.hasAnyDevice) {
    return (
      <div className={`flex items-center justify-center gap-2 py-2 ${className}`}>
        <WifiOff size={14} className="text-white/20" />
        <span className="text-xs text-white/30">No wearables connected</span>
      </div>
    );
  }

  // Get top 2 metrics to display
  const keyMetrics = [];

  if (biometrics.devices.whoop?.connected) {
    keyMetrics.push({
      label: 'Recovery',
      value: biometrics.devices.whoop.recovery,
      suffix: '%',
      color: biometrics.devices.whoop.color,
    });
  }
  if (biometrics.devices.oura?.connected) {
    keyMetrics.push({
      label: 'Sleep',
      value: biometrics.devices.oura.sleepScore,
      suffix: '',
      color: biometrics.devices.oura.color,
    });
  }
  if (biometrics.devices.neuphoria?.connected && keyMetrics.length < 2) {
    keyMetrics.push({
      label: 'Focus',
      value: biometrics.devices.neuphoria.focusScore,
      suffix: '',
      color: biometrics.devices.neuphoria.color,
    });
  }

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Device badges */}
      <div className="flex items-center gap-1">
        {Object.entries(biometrics.devices).map(([device, data]) => (
          <DeviceBadge key={device} device={device} data={data} />
        ))}
      </div>

      {/* Key metrics */}
      {keyMetrics.length > 0 && (
        <div className="flex items-center gap-2 ml-auto">
          {keyMetrics.slice(0, 2).map((metric, idx) => (
            <div
              key={idx}
              className="flex items-center gap-1 px-2 py-0.5 rounded bg-white/[0.04]"
            >
              <span className="text-[10px] text-white/40">{metric.label}</span>
              <span
                className="text-sm font-semibold"
                style={{ color: metric.color }}
              >
                {metric.value}{metric.suffix}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * BiometricBadgesCard - Expanded card view with all device details
 * For use in player detail views
 */
export const BiometricBadgesCard = ({ playerId, playerName = '', className = '' }) => {
  const biometrics = useMemo(
    () => generateBiometricProfile(playerId, playerName),
    [playerId, playerName]
  );

  const statusColors = getStatusColor(biometrics.overallStatus);

  return (
    <div className={`p-4 rounded-xl bg-white/[0.02] border border-white/[0.08] ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heart size={16} className="text-red-400" />
          <span className="text-sm font-medium text-white">Wearables</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColors.bg} ${statusColors.text} ${statusColors.border} border`}>
            {biometrics.activeDeviceCount}/{biometrics.totalDevices} active
          </span>
          <span className="text-[10px] text-white/30">{biometrics.lastSync}</span>
        </div>
      </div>

      {!biometrics.hasAnyDevice ? (
        <div className="text-center py-6">
          <WifiOff size={32} className="mx-auto mb-2 text-white/10" />
          <p className="text-sm text-white/40">No wearable devices connected</p>
          <p className="text-xs text-white/20 mt-1">Connect Whoop, Oura, or Neuphoria to track biometrics</p>
        </div>
      ) : (
        <>
          {/* Composite Readiness */}
          {biometrics.compositeReadiness && (
            <div className="flex items-center justify-between mb-4 p-3 rounded-lg bg-white/[0.04]">
              <span className="text-sm text-white/60">Composite Readiness</span>
              <ReadinessScore score={biometrics.compositeReadiness} size="lg" />
            </div>
          )}

          {/* Device Grid */}
          <div className="grid grid-cols-2 gap-3">
            {/* Whoop */}
            <DeviceCard
              device={biometrics.devices.whoop}
              icon={<Zap size={14} />}
              metrics={biometrics.devices.whoop?.connected ? [
                { label: 'Recovery', value: biometrics.devices.whoop.recovery, suffix: '%' },
                { label: 'Strain', value: biometrics.devices.whoop.strain },
                { label: 'HRV', value: biometrics.devices.whoop.hrv, suffix: 'ms' },
              ] : []}
            />

            {/* Oura */}
            <DeviceCard
              device={biometrics.devices.oura}
              icon={<Moon size={14} />}
              metrics={biometrics.devices.oura?.connected ? [
                { label: 'Readiness', value: biometrics.devices.oura.readiness },
                { label: 'Sleep', value: biometrics.devices.oura.sleepScore },
                { label: 'Temp', value: biometrics.devices.oura.bodyTemp, suffix: '\u00B0' },
              ] : []}
            />

            {/* Neuphoria */}
            <DeviceCard
              device={biometrics.devices.neuphoria}
              icon={<Brain size={14} />}
              metrics={biometrics.devices.neuphoria?.connected ? [
                { label: 'Mental', value: biometrics.devices.neuphoria.mentalState },
                { label: 'Focus', value: biometrics.devices.neuphoria.focusScore },
                { label: 'UMPI', value: biometrics.devices.neuphoria.umpiScore },
              ] : []}
            />

            {/* Garmin */}
            <DeviceCard
              device={biometrics.devices.garmin}
              icon={<Activity size={14} />}
              metrics={biometrics.devices.garmin?.connected ? [
                { label: 'VO2', value: biometrics.devices.garmin.vo2Max },
                { label: 'Battery', value: biometrics.devices.garmin.bodyBattery, suffix: '%' },
                { label: 'Load', value: biometrics.devices.garmin.trainingLoad },
              ] : []}
            />
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Individual device card within BiometricBadgesCard
 */
const DeviceCard = ({ device, icon, metrics }) => {
  if (!device) return null;

  const isConnected = device.connected;

  return (
    <div
      className={`p-3 rounded-lg border transition-all ${
        isConnected
          ? 'bg-white/[0.04] border-white/[0.08] hover:border-white/[0.15]'
          : 'bg-white/[0.01] border-white/[0.04]'
      }`}
    >
      {/* Device header */}
      <div className="flex items-center gap-2 mb-2">
        <div
          className={`p-1.5 rounded-md ${isConnected ? '' : 'opacity-30'}`}
          style={{ backgroundColor: isConnected ? `${device.color}20` : 'transparent' }}
        >
          <span style={{ color: isConnected ? device.color : 'rgba(255,255,255,0.2)' }}>
            {icon}
          </span>
        </div>
        <span className={`text-xs font-medium ${isConnected ? 'text-white' : 'text-white/30'}`}>
          {device.deviceName}
        </span>
        {isConnected ? (
          <Wifi size={10} className="ml-auto text-green-400" />
        ) : (
          <WifiOff size={10} className="ml-auto text-white/20" />
        )}
      </div>

      {/* Metrics */}
      {isConnected && metrics.length > 0 ? (
        <div className="grid grid-cols-3 gap-1">
          {metrics.map((metric, idx) => (
            <div key={idx} className="text-center">
              <div className="text-sm font-semibold text-white">
                {metric.value}{metric.suffix || ''}
              </div>
              <div className="text-[9px] text-white/40 uppercase">{metric.label}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-2">
          <span className="text-[10px] text-white/20">Not connected</span>
        </div>
      )}
    </div>
  );
};

/**
 * BiometricStatusDot - Simple status indicator
 */
export const BiometricStatusDot = ({ playerId, size = 'md' }) => {
  const biometrics = useMemo(
    () => generateBiometricProfile(playerId),
    [playerId]
  );

  if (!biometrics.hasAnyDevice) {
    return null;
  }

  const statusColors = getStatusColor(biometrics.overallStatus);
  const sizeClass = size === 'lg' ? 'w-3 h-3' : size === 'sm' ? 'w-1.5 h-1.5' : 'w-2 h-2';

  return (
    <div
      className={`${sizeClass} rounded-full ${statusColors.bg.replace('/20', '')} animate-pulse`}
      title={`Readiness: ${biometrics.compositeReadiness || 'N/A'}%`}
    />
  );
};

// Default export
export default BiometricBadgesCompact;
